<template>
  <SmDialog
    v-model:visibility="visible"
    :title="i18n.t('datev.editImport')"
    size="medium">
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-contrast">
          {{ i18n.t('name') }}
        </span>
        <SmInput
          v-model="form.name"
          class="w-full"
          size="medium"
          design="form"
          outline
          :disabled="saving"
          :label="i18n.t('datev.nameExample')" />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-contrast">
          {{ i18n.t('description') }}
        </span>
        <SmInput
          v-model="form.description"
          class="w-full"
          size="medium"
          design="form"
          outline
          :disabled="saving"
          :label="i18n.t('datev.descriptionExample')" />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-contrast">
          {{ i18n.t('date') }}
        </span>
        <SmDatepicker
          v-model:date="form.date"
          class="w-full"
          :disabled="saving"
          :placeholder="i18n.t('pleaseSelectDate')"
          :min-date="null" />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-contrast">
          {{ i18n.t('tags') }}
        </span>
        <SmSelect
          v-model:selection="selectedTagOptions"
          class="w-full"
          :options="tagOptions"
          :label="i18n.t('chooseTags')"
          :disabled="saving"
          :multiselect="true"
          searchable>
          <template #item="{ item }">
            <div class="flex w-full items-center gap-2">
              <TagComponent v-if="tags[item.value]" :tag="tags[item.value]" />
              <span v-else>{{ item.label }}</span>
            </div>
          </template>
        </SmSelect>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-2">
        <SmButton outline :disabled="saving" @click="visible = false">
          {{ i18n.t('cancel') }}
        </SmButton>
        <SmButton
          type="primary"
          :loading="saving"
          :disabled="saving || !isDirty"
          @click="handleSave">
          {{ i18n.t('general.save') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  import {
    AccountingImportOut,
    AccountingImportUpdateIn,
    AccountingService,
    Tag,
  } from '@/client'
  import SmDialog from '@/components/sm/SmDialog.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmDatepicker from '@/components/sm/SmDatepicker.vue'
  import SmSelect from '@/components/sm/SmSelect.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import TagComponent from '@/components/tags/TagComponent.vue'
  import { sendToast } from '@/components/sm/SmNotification'

  const props = defineProps<{
    visible: boolean
    importRecord: AccountingImportOut | null
    availableTags: Tag[]
  }>()

  const emit = defineEmits<{
    (event: 'update:visible', value: boolean): void
    (event: 'updated'): void
  }>()

  const i18n = useI18n()

  const visible = computed({
    get: () => props.visible,
    set: (value: boolean) => emit('update:visible', value),
  })

  const tags = computed<Record<string, Tag>>(() =>
    props.availableTags.reduce(
      (acc, tag) => {
        acc[tag._id] = tag
        return acc
      },
      {} as Record<string, Tag>
    )
  )

  const tagsByName = computed<Record<string, Tag>>(() =>
    props.availableTags.reduce(
      (acc, tag) => {
        acc[tag.name] = tag
        return acc
      },
      {} as Record<string, Tag>
    )
  )

  const tagOptions = computed(() =>
    props.availableTags.map((tag) => ({
      label: tag.name,
      value: tag._id,
      key: tag._id,
    }))
  )

  const form = ref({
    name: '',
    description: '',
    date: undefined as Date | undefined,
    tags: [] as string[],
  })

  const selectedTagOptions = computed({
    get: () =>
      tagOptions.value.filter((option) =>
        form.value.tags.includes(option.value)
      ),
    set: (value: typeof tagOptions.value) => {
      form.value.tags = value.map((option) => option.value)
    },
  })

  const saving = ref(false)
  const initialSnapshot = ref('')

  function normalizeTagValue(tagValue: string): string {
    if (tags.value[tagValue]) {
      return tags.value[tagValue]._id
    }
    if (tagsByName.value[tagValue]) {
      return tagsByName.value[tagValue]._id
    }
    return tagValue
  }

  function initializeForm() {
    if (!props.importRecord) return

    form.value.name = props.importRecord.name ?? ''
    form.value.description = props.importRecord.description ?? ''
    form.value.date = props.importRecord.date
      ? new Date(props.importRecord.date)
      : undefined
    form.value.tags = (props.importRecord.tags ?? []).map((tag) =>
      normalizeTagValue(tag)
    )

    initialSnapshot.value = createSnapshot()
  }

  watch(
    () => props.importRecord,
    () => {
      if (visible.value) {
        initializeForm()
      }
    }
  )

  watch(
    () => props.visible,
    (val) => {
      if (val) {
        initializeForm()
      } else {
        initialSnapshot.value = createSnapshot()
      }
    }
  )

  watch(
    () => props.availableTags,
    () => {
      if (visible.value && props.importRecord) {
        form.value.tags = (props.importRecord.tags ?? []).map((tag) =>
          normalizeTagValue(tag)
        )
      }
    },
    { deep: true }
  )

  async function handleSave() {
    if (saving.value || !props.importRecord || !isDirty.value) return

    saving.value = true
    try {
      const payload: AccountingImportUpdateIn = {
        name: form.value.name || null,
        description: form.value.description || null,
        date: form.value.date ? form.value.date.toISOString() : null,
        tags: form.value.tags,
      }

      await AccountingService.patchAccountingImportApiV1AccountingAccountingImportsAccountingImportIdPatch(
        {
          accountingImportId: props.importRecord._id,
          requestBody: payload,
        }
      )

      sendToast(i18n.t('datev.updateSuccess'), undefined, 'success')
      initialSnapshot.value = createSnapshot()
      emit('updated')
      visible.value = false
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('datev.updateError'), undefined, 'error')
    } finally {
      saving.value = false
    }
  }

  function createSnapshot(): string {
    const normalized = {
      name: form.value.name ?? '',
      description: form.value.description ?? '',
      date: form.value.date ? form.value.date.toISOString() : null,
      tags: [...form.value.tags].sort(),
    }
    return JSON.stringify(normalized)
  }

  const isDirty = computed(() => createSnapshot() !== initialSnapshot.value)

  onMounted(() => {
    if (visible.value) {
      initializeForm()
    }
  })
</script>
